import * as THREE from "three";
import type { SpawnedCoin, WordGate } from "../types";
import type { WordDTO } from "@shared/types";
import { LANE_POSITIONS } from "@shared/constants";
import { createRng } from "@shared/game";
import { createObstacle, type ObstacleController, type ObstacleKind } from "./obstacles";
import type { Path } from "./Path";

export interface SpawnPlan {
  obstacles: ObstacleController[];
  coins: SpawnedCoin[];
  gates: WordGate[];
}

const OBSTACLE_MIX: { kind: ObstacleKind; weight: number }[] = [
  { kind: "barrier", weight: 3 },
  { kind: "crate", weight: 3 },
  { kind: "boulder", weight: 2 },
  { kind: "log", weight: 2 },
  { kind: "sweeper", weight: 2 },
  { kind: "swing-log", weight: 1 },
  { kind: "lava", weight: 1 },
  { kind: "spike-roller", weight: 1 },
];

function pickKind(rng: () => number): ObstacleKind {
  const total = OBSTACLE_MIX.reduce((a, b) => a + b.weight, 0);
  let r = rng() * total;
  for (const o of OBSTACLE_MIX) {
    if ((r -= o.weight) <= 0) return o.kind;
  }
  return "barrier";
}

export function buildSpawnPlan(opts: {
  seed: number;
  courseLength: number;
  objectsCount: number;
  coinsCount: number;
  words: WordDTO[];
  questionsCount: number;
  path: Path;
}): SpawnPlan {
  const rng = createRng(opts.seed);
  const startPad = Math.min(48, Math.max(32, opts.courseLength * 0.12));
  const endPad = Math.min(220, Math.max(56, opts.courseLength * 0.22));
  const usable = Math.max(90, opts.courseLength - startPad - endPad);

  const gateWords = opts.words.slice(0, opts.questionsCount);
  const gateStep = usable / (gateWords.length + 1);
  const gates: WordGate[] = gateWords.map((word, i) => ({
    id: i,
    z: placeAwayFromBridge(startPad + gateStep * (i + 1), opts.path, opts.courseLength),
    word,
    triggered: false,
  }));

  const obstacles: ObstacleController[] = [];
  let oid = 0;
  const sweeperZ = startPad + usable * 0.42;
  obstacles.push(createObstacle("sweeper", { id: oid++, z: sweeperZ, lane: 2, path: opts.path }));
  const bridge = opts.path.narrowBridgeSections()[0];
  if (bridge) {
    obstacles.push(createObstacle("swing-log", { id: oid++, z: bridge.center, lane: 2, path: opts.path }));
  }
  const cannonSlots = [startPad + usable * 0.28, startPad + usable * 0.66];
  for (let i = 0; i < cannonSlots.length; i++) {
    let s = cannonSlots[i]!;
    for (let n = 0; n < 6; n++) {
      const tooClose = gates.some((g) => Math.abs(g.z - s) < 15)
        || obstacles.some((o) => Math.abs(o.getZ() - s) < 12)
        || opts.path.isNearCorner(s, 12);
      if (!tooClose) break;
      s += 8;
    }
    if (s < startPad + 8 || s > opts.courseLength - endPad - 8) continue;
    obstacles.push(createObstacle("boulder", { id: oid++, z: s, lane: i % 2 === 0 ? 1 : 3, path: opts.path }));
  }
  let attempts = 0;
  // Multi-lane obstacles (lava, log, spike-roller) need a much wider corner
  // buffer because their lateral extent reaches almost path-edge — on a tight
  // bend the outside end can clip past the road tile.
  const cornerBuffer = (k: ObstacleKind) =>
    k === "lava" || k === "log" || k === "spike-roller" || k === "sweeper" || k === "swing-log" ? 16 : 9;
  while (obstacles.length < opts.objectsCount && attempts < opts.objectsCount * 8) {
    attempts++;
    const s = startPad + rng() * usable;
    if (gates.some((g) => Math.abs(g.z - s) < 14)) continue;
    if (obstacles.some((o) => Math.abs(o.getZ() - s) < 9)) continue;
    if (opts.path.narrowBridgeHalfWidthAt(s) != null) continue;
    const kind = pickKind(rng);
    if (opts.path.isNearCorner(s, cornerBuffer(kind))) continue;
    const lane = Math.floor(rng() * LANE_POSITIONS.length);
    obstacles.push(createObstacle(kind, { id: oid++, z: s, lane, path: opts.path }));
  }

  const coins: SpawnedCoin[] = [];
  let cid = 0;
  let placed = 0;
  while (placed < opts.coinsCount) {
    const clusterSize = 3 + Math.floor(rng() * 3);
    const lane = Math.floor(rng() * LANE_POSITIONS.length);
    const s = startPad + rng() * usable;
    if (opts.path.narrowBridgeHalfWidthAt(s) != null) continue;
    if (gates.some((g) => Math.abs(g.z - s) < 10)) continue;
    if (obstacles.some((o) => o.lanes().includes(lane) && Math.abs(o.getZ() - s) < 9)) continue;
    // Coins can sit through corners (they're visual rewards, not hazards).
    for (let i = 0; i < clusterSize && placed < opts.coinsCount; i++) {
      coins.push({ id: cid++, z: s + i * 1.5, lane, collected: false });
      placed++;
    }
  }

  return { obstacles, coins, gates };
}

function placeAwayFromBridge(s: number, path: Path, courseLength: number) {
  const bridge = path.narrowBridgeSections()[0];
  if (!bridge || s < bridge.start - 14 || s > bridge.end + 14) return s;
  const before = bridge.start - 18;
  const after = bridge.end + 18;
  return THREE.MathUtils.clamp(
    Math.abs(s - before) < Math.abs(s - after) ? before : after,
    38,
    courseLength - 38,
  );
}

export function buildCoinMesh(c: SpawnedCoin, path?: Path): THREE.Object3D {
  const m = new THREE.Mesh(
    new THREE.CylinderGeometry(0.3, 0.3, 0.08, 16),
    new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.5, roughness: 0.3 }),
  );
  // Coin disc faces the player (perpendicular to path tangent).
  if (path) {
    const pos = path.positionAt(c.z, LANE_POSITIONS[c.lane]!);
    m.position.copy(pos);
    m.position.y = 1.0;
    m.rotation.y = path.yawAt(c.z);
    m.rotation.z = Math.PI / 2;
  } else {
    m.rotation.z = Math.PI / 2;
    m.position.set(LANE_POSITIONS[c.lane]!, 1.0, c.z);
  }
  m.castShadow = true;
  return m;
}

export function buildGateMesh(g: WordGate, path?: Path): THREE.Object3D {
  const grp = new THREE.Group();
  if (path) {
    const pos = path.positionAt(g.z, 0);
    grp.position.copy(pos);
    grp.rotation.y = path.yawAt(g.z);
  } else {
    grp.position.set(0, 0, g.z);
  }
  const beamMat = new THREE.MeshStandardMaterial({
    color: 0x9b5cf6,
    emissive: 0x6f3bd8,
    emissiveIntensity: 0.5,
  });
  const arch = new THREE.Mesh(new THREE.BoxGeometry(13, 0.4, 0.4), beamMat);
  arch.position.y = 4.2;
  grp.add(arch);
  for (const x of [-6.4, 6.4]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.4, 4.2, 0.4), beamMat);
    post.position.set(x, 2.1, 0);
    grp.add(post);
  }
  const ribbon = new THREE.Mesh(
    new THREE.BoxGeometry(12.2, 0.8, 0.05),
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85 }),
  );
  ribbon.position.y = 4.2;
  ribbon.position.z = 0.25;
  grp.add(ribbon);
  return grp;
}
